import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ProfileDropdown } from './Profile';
import { useUser } from '../contexts/UserContext';
import { useTutorialVideos } from '../contexts/TutorialVideoContext';
import { logout } from '../services/api';
import { HelpDrawer } from './Help';

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const { clearUser, isAuthenticated, isAdmin } = useUser();
  const { clearVideos } = useTutorialVideos();

  const handleLogout = () => {
    logout();
    clearUser();
    clearVideos();
    setIsMobileMenuOpen(false);
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Note: Scoring Builder (/stock-analyzer/scoring-builder) is intentionally
  // omitted from the nav while it's being polished. Admins reach it via the
  // CTA button on the Stock Analyzer page or by typing the URL directly;
  // non-admin users are blocked by FeatureGate.
  const navLinks = [
    { to: '/', label: 'Screener' },
    { to: '/0dte', label: '0DTE' },
    { to: '/watchlist', label: 'Watchlist' },
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/stock-analyzer', label: 'Stock Analyzer' },
    { to: '/research', label: 'Research' },
    { to: '/admin-tests', label: 'Admin Tests', adminOnly: true },
    { to: '/dashboard-builder', label: 'Dashboard Builder' },
  ].filter(link => !link.adminOnly || isAdmin);

  return (
    <nav className="bg-[#111] border-b border-muted p-4 flex justify-between items-center container-wide relative">
      <Link to="/" aria-label="Options Leveling home" className="flex items-center">
        <img src="/logo/logo_navbar-dark.svg" alt="Options Leveling" className="h-9 w-auto" />
      </Link>

      {/* Desktop Navigation - hidden on mobile */}
      <div className="hidden md:flex gap-6 items-center">
        {navLinks.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={`text-text hover:text-primary transition ${
              location.pathname === link.to ? 'text-primary' : ''
            }`}
          >
            {link.label}
          </Link>
        ))}

        {!isAuthenticated ? (
          <Link
            to="/login"
            className="text-text hover:text-primary transition"
          >
            Login
          </Link>
        ) : (
          <>
            <button
              onClick={() => setIsHelpDrawerOpen(true)}
              className="text-text hover:text-primary transition flex items-center gap-1"
              aria-label="Open help panel"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="hidden lg:inline">Help</span>
            </button>
            <ProfileDropdown />
          </>
        )}
      </div>

      {/* Hamburger Menu Button - visible only on mobile */}
      <button
        className="md:hidden text-text hover:text-primary transition p-2"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {isMobileMenuOpen ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          )}
        </svg>
      </button>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop - click to close */}
          <div
            className="absolute inset-0 bg-black/80"
            onClick={closeMobileMenu}
          />

          {/* Menu Panel - full width dropdown from top */}
          <div className="absolute top-0 left-0 right-0 bg-[#111] border-b border-muted shadow-xl">
            {/* Header with logo and close button */}
            <div className="flex justify-between items-center p-4 border-b border-muted">
              <Link
                to="/"
                onClick={closeMobileMenu}
                aria-label="Options Leveling home"
                className="flex items-center"
              >
                <img src="/logo/logo_navbar-dark.svg" alt="Options Leveling" className="h-7 w-auto" />
              </Link>
              <button
                onClick={closeMobileMenu}
                className="text-text hover:text-primary transition p-2"
                aria-label="Close menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Mobile Nav Links - full width, centered text */}
            <div className="flex flex-col">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={closeMobileMenu}
                  className={`px-6 py-4 text-lg text-center transition border-b border-muted/30 ${
                    location.pathname === link.to
                      ? 'text-primary bg-primary/10'
                      : 'text-text hover:text-primary hover:bg-surface'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {!isAuthenticated ? (
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="px-6 py-4 text-lg text-center text-text hover:text-primary hover:bg-surface transition border-b border-muted/30"
                >
                  Login
                </Link>
              ) : (
                <>
                  <Link
                    to="/profile"
                    onClick={closeMobileMenu}
                    className={`px-6 py-4 text-lg text-center transition border-b border-muted/30 ${
                      location.pathname === '/profile'
                        ? 'text-primary bg-primary/10'
                        : 'text-text hover:text-primary hover:bg-surface'
                    }`}
                  >
                    Account Settings
                  </Link>
                  <button
                    onClick={() => {
                      closeMobileMenu();
                      setIsHelpDrawerOpen(true);
                    }}
                    className="px-6 py-4 text-lg text-center text-text hover:text-primary hover:bg-surface transition border-b border-muted/30 w-full"
                  >
                    Help
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-6 py-4 text-lg text-center text-text hover:text-red-500 hover:bg-red-500/10 transition w-full"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <HelpDrawer
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}
      />
    </nav>
  );
}

export default Navbar;
